import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useGetResourceTypesQuery,
  useNewResourceTemplateMutation,
} from "../../services/appln";
import { isPositive, setDifference } from "../util";
import Plane from "../../components/CustomFields/Plane";

interface FormData {
  name: string;
  assessments: any;
}

interface Props {
  onSubmitHandler: (newObj: any) => void;
  onCancelHandler: () => void;
  state: boolean;
}

export default function NewResourceTemplate(props: Props) {
  const getDefaultValues = () => {
    return { resourceType: "", count: "" };
  };

  const [createTemplate, createTemplateParams] =
    useNewResourceTemplateMutation();

  const [resourceTypeMap, setResourceTypeMap] = React.useState(new Map());

  // saving assessments outside formik values since the one inside values cannot be
  // used to remove already specified designations.
  const [assessments, setAssessments] = React.useState([getDefaultValues()]);

  const getResourceTypes = () => {
    const usedResourceTypes = assessments.map((e: any) => {
      return e["resourceType"];
    });
    return setDifference(Array.from(resourceTypeMap.keys()), usedResourceTypes);
  };

  const metaData = [
    {
      id: "resourceType",
      type: "autocomplete",
      options: getResourceTypes(),
      width: 300,
      label: strings.resourceType,
    },
    {
      id: "count",
      type: "text",
      width: 100,
      label: strings.count,
    },
  ];

  const validate = (assessments: any) => {
    console.log("validate, assessments:", assessments);
    const errors = [];
    if (assessments.length === 0) {
      errors.push(strings.formatString(strings.assessmentsEmpty));
    }
    for (let i = 0; i < assessments.length; i++) {
      if (!assessments[i].resourceType) {
        errors.push(strings.formatString(strings.resourceTypeEmpty, i + 1));
      }
      if (assessments[i].count === "") {
        errors.push(strings.formatString(strings.countEmpty, i + 1));
      } else if (!isPositive(assessments[i].count)) {
        errors.push(strings.formatString(strings.invalidCount, i + 1));
      }
    }
    console.log("validate, errors:", errors);
    return errors.length > 0 ? errors.join("\n") : undefined;
  };

  const resourceTypeApiResponse = useGetResourceTypesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });

  React.useEffect(() => {
    if (resourceTypeApiResponse.isSuccess) {
      setResourceTypeMap(
        new Map(
          resourceTypeApiResponse.data.data.map((e: any) => {
            return [e.name, e.id];
          }),
        ),
      );
    }
  }, [resourceTypeApiResponse]);

  const iv: FormData = {
    name: "",
    assessments: [getDefaultValues()],
  };

  const onCancelHandlerWrapper = () => {
    createTemplateParams.reset();
    props.onCancelHandler();
  };

  console.log("createTemplateParams", createTemplateParams);
  React.useEffect(() => {
    if (createTemplateParams.isSuccess) {
      setAssessments([getDefaultValues()]);
      props.onSubmitHandler(createTemplateParams.data.id);
    }
  }, [createTemplateParams]);

  return (
    <div>
      <Dialog
        open={props.state}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{strings.newTemplate}</DialogTitle>
        {createTemplateParams.isError && (
          <ErrorMsg msg={strings.failedToCreateTemplate} />
        )}
        {createTemplateParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              // console.log("validate, values:", values);
              // if (!values.name) {
              //   errors.name = strings.nameMandatory;
              // }
              // const assessmentErrors = validate(values.assessments);
              // if (assessmentErrors !== undefined) {
              //   errors.assessments = validate(values.assessments);
              // }

              // console.log("formik validate, errors:", errors);
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              console.log("validate, values:", values);
              console.log("validate, resourceTypeMap:", resourceTypeMap);
              setSubmitting(false);
              const new_assessments = values.assessments.map(
                (assessment: any) => {
                  return {
                    count: assessment.count,
                    resourceTypeId: resourceTypeMap.get(
                      assessment.resourceType,
                    ),
                  };
                },
              );
              values.assessments = new_assessments;
              createTemplate(values);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Plane
                        metaData={metaData}
                        data={values.assessments}
                        validationErrors={errors.assessments}
                        onAdd={(assessments: any) => {
                          setFieldValue("assessments", [
                            ...assessments,
                            getDefaultValues(),
                          ]);
                        }}
                        onChange={(assessments: any) => {
                          console.log(
                            "NewTemplateDialog, assessments:",
                            assessments,
                          );
                          setFieldValue("assessments", assessments);
                          // setAssessments(assessments);
                        }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.create}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
